import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';

import connection from '~services/connectionModule';

interface UseEmailDraftsQueryParams<T, E extends object> {
  enabled: boolean;
  errorMessage: string;
  fetchDrafts: (signal: AbortSignal) => Promise<{ drafts: T[] } & E>;
  keepPreviousData?: boolean;
  queryKey: unknown[];
}

export default function useEmailDraftsQuery<T, E extends object = object>({
  enabled,
  errorMessage,
  fetchDrafts,
  keepPreviousData: shouldKeepPreviousData = false,
  queryKey,
}: UseEmailDraftsQueryParams<T, E>) {
  const { data, isError, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: enabled
      ? async ({ signal }) => {
          try {
            return await fetchDrafts(signal);
          } catch (error) {
            connection.handleRequestError(error, errorMessage);
            throw error;
          }
        }
      : skipToken,
    // Each dialog open seeds its form from one fresh fetch: a cached preview could carry
    // stale recipients, and a refetch after the form is seeded would be ignored.
    gcTime: 0,
    // Keeps the rendered rows (and the user's edits) mounted while a changed option refetches.
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    retry: false,
    staleTime: Infinity,
  });

  return {
    data: data ?? null,
    drafts: data?.drafts ?? null,
    isError,
    isLoading,
    refetch,
  };
}
